import { Star } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Testimonial {
    id: string;
    rating: number;
    content: string;
    author: {
        name: string;
        role: string;
        location: string;
        imageUrl: string;
    };
}

export default function TestimonialsSlider() {
    const { t } = useTranslation();
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [current, setCurrent] = useState(0);

    const testimonials = t('home.testimonialsSlider.testimonials', { returnObjects: true }) as Testimonial[];
    const count = testimonials?.length ?? 0;

    const updateCurrent = useCallback(() => {
        const scroller = scrollerRef.current;

        if (!scroller || scroller.clientWidth === 0) {
            return;
        }

        setCurrent(Math.round(scroller.scrollLeft / scroller.clientWidth));
    }, []);

    useEffect(() => {
        const scroller = scrollerRef.current;

        if (!scroller) {
            return;
        }

        scroller.addEventListener('scroll', updateCurrent, { passive: true });
        updateCurrent();

        return () => scroller.removeEventListener('scroll', updateCurrent);
    }, [updateCurrent]);

    const goToSlide = (index: number) => {
        const scroller = scrollerRef.current;

        if (!scroller) {
            return;
        }

        scroller.scrollTo({ left: index * scroller.clientWidth, behavior: 'smooth' });
    };
    const previousSlide = () => goToSlide(count > 0 ? (current - 1 + count) % count : 0);
    const nextSlide = () => goToSlide(count > 0 ? (current + 1) % count : 0);

    return (
        <section className="landing-defer-paint relative isolate bg-linear-to-b from-gray-50 to-white py-12 sm:py-16 lg:py-24 dark:from-gray-950 dark:to-gray-900">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-4xl text-center">
                    <h2 className="text-sm font-semibold tracking-wide text-hs-blue uppercase dark:text-hs-green-light">
                        {t('home.testimonialsSlider.title')}
                    </h2>
                    <h2 className="mt-4 text-4xl font-semibold tracking-tight text-pretty text-headline sm:text-5xl lg:text-balance">
                        {t('home.testimonialsSlider.subtitle')}
                    </h2>
                </div>

                <div className="mx-auto mt-16 max-w-5xl">
                    <div className="relative sm:px-16">
                        <div
                            ref={scrollerRef}
                            className="landing-snap-scroller flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain scroll-smooth"
                            role="region"
                            aria-roledescription="carousel"
                            aria-label={t('home.testimonialsSlider.subtitle')}
                        >
                            {testimonials?.map((testimonial) => (
                                <div key={testimonial.id} className="w-full shrink-0 snap-center px-4" role="group" aria-roledescription="slide">
                                    <div className="mx-auto max-w-4xl">
                                        <figure className="rounded-2xl border border-gray-200 bg-white p-8 shadow-xs ring-0 md:p-12 dark:border-gray-700/50 dark:bg-gray-800">
                                            <div className="mb-6 flex justify-center">
                                                <div className="flex items-center gap-1">{renderStars(testimonial.rating)}</div>
                                            </div>

                                            <blockquote className="text-center text-lg leading-relaxed font-medium text-paragraph sm:text-xl">
                                                <p>„{testimonial.content}"</p>
                                            </blockquote>

                                            <figcaption className="mt-8 flex flex-col items-center sm:flex-row sm:items-center sm:justify-center sm:gap-x-6">
                                                {testimonial.author.imageUrl ? (
                                                    <TestimonialAvatar src={testimonial.author.imageUrl} alt={testimonial.author.name} />
                                                ) : (
                                                    <div className="mb-4 flex size-16 shrink-0 items-center justify-center rounded-full bg-hs-blue/10 sm:mb-0 dark:bg-hs-blue/20">
                                                        <span className="text-lg font-semibold text-hs-blue dark:text-hs-blue-light">
                                                            {testimonial.author.name
                                                                .split(' ')
                                                                .map((n: string) => n[0])
                                                                .join('')
                                                                .slice(0, 2)
                                                                .toUpperCase()}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="text-center sm:text-left">
                                                    <div className="text-base leading-relaxed font-semibold text-headline">
                                                        {testimonial.author.name}
                                                    </div>
                                                    <div className="text-base leading-relaxed text-paragraph">
                                                        {testimonial.author.role}
                                                        {testimonial.author.location
                                                            ? ` ${t('home.testimonialsSlider.locationPreposition')} ${testimonial.author.location}`
                                                            : ''}
                                                    </div>
                                                </div>
                                            </figcaption>
                                        </figure>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button
                            type="button"
                            onClick={previousSlide}
                            className="absolute top-1/2 -left-6 hidden size-8 -translate-y-1/2 items-center justify-center rounded-full border border-input bg-background shadow-xs hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 sm:flex dark:bg-gray-600 dark:hover:bg-gray-500"
                            disabled={count < 2}
                            aria-label={t('common.ui.previousSlide')}
                        >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <button
                            type="button"
                            onClick={nextSlide}
                            className="absolute top-1/2 -right-6 hidden size-8 -translate-y-1/2 items-center justify-center rounded-full border border-input bg-background shadow-xs hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 sm:flex dark:bg-gray-600 dark:hover:bg-gray-500"
                            disabled={count < 2}
                            aria-label={t('common.ui.nextSlide')}
                        >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    <div className="relative mt-8 flex justify-center">
                        <button
                            type="button"
                            onClick={previousSlide}
                            className="absolute top-1/2 left-4 flex size-8 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:hidden dark:border-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500"
                            disabled={count < 2}
                            aria-label={t('common.ui.previousSlide')}
                        >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <button
                            type="button"
                            onClick={nextSlide}
                            className="absolute top-1/2 right-4 flex size-8 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:hidden dark:border-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500"
                            disabled={count < 2}
                            aria-label={t('common.ui.nextSlide')}
                        >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>

                        <div className="flex space-x-2">
                            {testimonials?.map((testimonial, index) => (
                                <button
                                    key={testimonial.id}
                                    type="button"
                                    onClick={() => goToSlide(index)}
                                    className={`h-3 w-3 rounded-full transition-colors ${
                                        current === index
                                            ? 'bg-hs-blue dark:bg-hs-blue-light'
                                            : 'bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500'
                                    }`}
                                    aria-label={`${t('home.testimonialsSlider.title')} ${index + 1}`}
                                    aria-current={current === index ? 'true' : undefined}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function TestimonialAvatar({ src, alt }: { src: string; alt: string }) {
    const base = src.replace(/\.(png|webp|avif)$/i, '');

    return (
        <picture>
            <source type="image/avif" srcSet={`${base}.avif`} />
            <source type="image/webp" srcSet={`${base}.webp`} />
            <img
                alt={alt}
                src={`${base}.webp`}
                className="mb-4 size-16 rounded-full bg-gray-50 sm:mb-0 dark:bg-gray-700"
                loading="lazy"
                decoding="async"
                width={64}
                height={64}
            />
        </picture>
    );
}

function renderStars(rating: number) {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
        stars.push(<Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />);
    }

    if (hasHalfStar) {
        stars.push(
            <div key="half" className="relative">
                <Star className="h-5 w-5 text-yellow-400" />
                <div className="absolute inset-0 w-1/2 overflow-hidden">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                </div>
            </div>,
        );
    }

    const remainingStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < remainingStars; i++) {
        stars.push(<Star key={`empty-${i}`} className="h-5 w-5 text-yellow-400" />);
    }

    return stars;
}
